import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  AreaChart, Area, BarChart, Bar 
} from 'recharts';
import { 
  TrendingDown, TrendingUp, Cigarette, Brain, Smile, Activity, 
  ArrowLeft, Calendar, Award 
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Statistics = () => {
  const { language } = useLanguage();
  const [trackingData, setTrackingData] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [trackingRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/tracking/weekly`),
        axios.get(`${API_URL}/tracking/stats`)
      ]);
      
      setTrackingData(trackingRes.data.reverse());
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
      day: 'numeric',
      month: 'short'
    });
  };

  const chartData = trackingData.map(item => ({
    date: formatDate(item.week_start_date),
    cigarettes: item.cigarettes_per_day,
    anxiety: item.stress_level,
    craving: item.craving_level,
    mood: item.mood_level
  }));

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-[#F9FAFB]">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#F9FAFB] py-8 sm:py-12 px-4" data-testid="statistics-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 animate-fade-in-up">
          <div>
            <Link to="/dashboard">
              <Button variant="ghost" className="rounded-full mb-2">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Retour' : 'Back'}
              </Button>
            </Link>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
              {language === 'fr' ? 'Vos statistiques' : 'Your statistics'}
            </h1>
            <p className="text-gray-600 mt-1">
              {language === 'fr' ? 'Suivez votre progression vers une vie sans tabac' : 'Track your progress towards a smoke-free life'}
            </p>
          </div>
          <Link to="/weekly-tracking">
            <Button className="rounded-full bg-primary hover:bg-primary/90 shadow-lg" data-testid="new-tracking-btn">
              <Calendar className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Nouveau suivi' : 'New tracking'}
            </Button>
          </Link>
        </div>

        {trackingData.length === 0 ? (
          <Card className="glass-card rounded-3xl p-12 text-center animate-fade-in-up">
            <div className="w-20 h-20 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-6">
              <Activity className="w-10 h-10 text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {language === 'fr' ? 'Aucune donnée disponible' : 'No data available'}
            </h2>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              {language === 'fr' 
                ? 'Commencez votre suivi hebdomadaire pour voir vos statistiques et votre progression.'
                : 'Start your weekly tracking to see your statistics and progress.'}
            </p>
            <Link to="/weekly-tracking">
              <Button className="rounded-full bg-primary hover:bg-primary/90 shadow-lg px-8">
                {language === 'fr' ? 'Commencer le suivi' : 'Start tracking'}
              </Button>
            </Link>
          </Card>
        ) : (
          <>
            {/* Stats Overview */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="glass-card rounded-3xl p-6 animate-fade-in-up" data-testid="stat-weeks">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center">
                    <Calendar className="w-7 h-7 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {language === 'fr' ? 'Semaines suivies' : 'Weeks tracked'}
                    </p>
                    <p className="text-3xl font-bold text-gray-900">{stats?.total_weeks || 0}</p>
                  </div>
                </div>
              </Card>

              <Card className="glass-card rounded-3xl p-6 animate-fade-in-up animate-delay-100" data-testid="stat-reduction">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                    stats?.cigarette_reduction > 0 ? 'bg-green-100' : 'bg-orange-100'
                  }`}>
                    {stats?.cigarette_reduction > 0 ? (
                      <TrendingDown className="w-7 h-7 text-green-600" />
                    ) : (
                      <TrendingUp className="w-7 h-7 text-orange-600" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {language === 'fr' ? 'Réduction cigarettes' : 'Cigarette reduction'}
                    </p>
                    <p className={`text-3xl font-bold ${stats?.cigarette_reduction > 0 ? 'text-green-600' : 'text-orange-600'}`}>
                      {stats?.cigarette_reduction > 0 ? '-' : '+'}{Math.abs(stats?.cigarette_reduction || 0)}
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="glass-card rounded-3xl p-6 animate-fade-in-up animate-delay-200" data-testid="stat-craving">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center">
                    <Brain className="w-7 h-7 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {language === 'fr' ? 'Envie moyenne' : 'Avg craving'}
                    </p>
                    <p className="text-3xl font-bold text-gray-900">{stats?.avg_craving?.toFixed(1) || 0}/10</p>
                  </div>
                </div>
              </Card>

              <Card className="glass-card rounded-3xl p-6 animate-fade-in-up animate-delay-300" data-testid="stat-mood">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-yellow-100 flex items-center justify-center">
                    <Smile className="w-7 h-7 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {language === 'fr' ? 'Humeur moyenne' : 'Avg mood'}
                    </p>
                    <p className="text-3xl font-bold text-gray-900">{stats?.avg_mood?.toFixed(1) || 0}/10</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Cigarettes Chart */}
              <Card className="glass-card rounded-3xl p-6 animate-fade-in-up" data-testid="chart-cigarettes">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                    <Cigarette className="w-5 h-5 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {language === 'fr' ? 'Cigarettes par jour' : 'Cigarettes per day'}
                  </h3>
                </div>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="colorCig" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="date" stroke="#9ca3af" fontSize={12} />
                      <YAxis stroke="#9ca3af" fontSize={12} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          borderRadius: '12px', 
                          border: 'none',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.1)' 
                        }} 
                      />
                      <Area 
                        type="monotone" 
                        dataKey="cigarettes" 
                        stroke="#ef4444" 
                        strokeWidth={3}
                        fill="url(#colorCig)" 
                        name={language === 'fr' ? 'Cigarettes' : 'Cigarettes'}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              {/* Anxiety/Stress Chart */}
              <Card className="glass-card rounded-3xl p-6 animate-fade-in-up animate-delay-100" data-testid="chart-anxiety">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                    <Brain className="w-5 h-5 text-orange-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {language === 'fr' ? 'Anxiété & Envies' : 'Anxiety & Cravings'}
                  </h3>
                </div>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="date" stroke="#9ca3af" fontSize={12} />
                      <YAxis stroke="#9ca3af" fontSize={12} domain={[0, 10]} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          borderRadius: '12px', 
                          border: 'none',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.1)' 
                        }} 
                      />
                      <Line 
                        type="monotone" 
                        dataKey="anxiety" 
                        stroke="#f97316" 
                        strokeWidth={3}
                        dot={{ fill: '#f97316', strokeWidth: 2 }}
                        name={language === 'fr' ? 'Anxiété' : 'Anxiety'}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="craving" 
                        stroke="#8b5cf6" 
                        strokeWidth={3}
                        dot={{ fill: '#8b5cf6', strokeWidth: 2 }}
                        name={language === 'fr' ? 'Envies' : 'Cravings'}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              {/* Mood Chart */}
              <Card className="glass-card rounded-3xl p-6 animate-fade-in-up animate-delay-200" data-testid="chart-mood">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                    <Smile className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {language === 'fr' ? 'Humeur' : 'Mood'}
                  </h3>
                </div>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="date" stroke="#9ca3af" fontSize={12} />
                      <YAxis stroke="#9ca3af" fontSize={12} domain={[0, 10]} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          borderRadius: '12px', 
                          border: 'none',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.1)' 
                        }} 
                      />
                      <Bar 
                        dataKey="mood" 
                        fill="#4A6741" 
                        radius={[8, 8, 0, 0]}
                        name={language === 'fr' ? 'Humeur' : 'Mood'}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              {/* Achievement Card */}
              <Card className="glass-card rounded-3xl p-6 animate-fade-in-up animate-delay-300" data-testid="achievements">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-yellow-100 flex items-center justify-center">
                    <Award className="w-5 h-5 text-yellow-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {language === 'fr' ? 'Vos accomplissements' : 'Your achievements'}
                  </h3>
                </div>
                
                <div className="space-y-4">
                  {stats?.total_weeks >= 1 && (
                    <div className="flex items-center gap-4 p-4 bg-green-50 rounded-2xl">
                      <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                        <span className="text-2xl">🌱</span>
                      </div>
                      <div>
                        <p className="font-semibold text-green-800">
                          {language === 'fr' ? 'Premier pas' : 'First step'}
                        </p>
                        <p className="text-sm text-green-600">
                          {language === 'fr' ? 'Première semaine de suivi complétée' : 'First week of tracking completed'}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {stats?.cigarette_reduction > 0 && (
                    <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-2xl">
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-2xl">📉</span>
                      </div>
                      <div>
                        <p className="font-semibold text-blue-800">
                          {language === 'fr' ? 'En progression' : 'Making progress'}
                        </p>
                        <p className="text-sm text-blue-600">
                          {language === 'fr' ? `Réduction de ${stats.cigarette_reduction} cigarettes` : `Reduced by ${stats.cigarette_reduction} cigarettes`}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {stats?.total_weeks >= 4 && (
                    <div className="flex items-center gap-4 p-4 bg-purple-50 rounded-2xl">
                      <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                        <span className="text-2xl">🏆</span>
                      </div>
                      <div>
                        <p className="font-semibold text-purple-800">
                          {language === 'fr' ? 'Persévérant' : 'Perseverant'}
                        </p>
                        <p className="text-sm text-purple-600">
                          {language === 'fr' ? '4 semaines de suivi régulier' : '4 weeks of regular tracking'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
